import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sb } from '../supabase';
import { getFactoryClaimPreview } from '../lib/factoryClaim';
import { resolveFactoryEntry, ENTRY } from '../lib/factoryEntry';
import { fetchLatestOwnerThreadForFactory } from '../lib/factoryChat';
import { waLink } from '../lib/maabarContact';
import { logClaimEvent } from '../lib/claimFunnel';

// Landing route for WhatsApp template 20 (message entry): /factory/inbox/:slug,
// where :slug is factory_directory.claim_slug. A factory owner tapping the button
// must always end on the conversation itself — never on a generic dashboard, and
// never on an "owned by another account" wall, because the person arriving here is
// almost always the owner.
//
// It resolves and redirects; it renders nothing but a spinner, except for the one
// state that genuinely needs a decision from the visitor (signed in as somebody
// else). Signed-out states hand off with ?next= pointing back HERE, so this route
// re-resolves after auth rather than trying to guess a thread id while anonymous.
const C = {
  zh: {
    working: '正在打开您的对话…',
    notFound: '此链接无效或已失效。',
    otherTitle: '此工厂主页已由其他账户注册',
    otherBody: '我们无法显示该账户的任何信息。如果这是贵公司的主页，请使用该账户登录；或通过 WhatsApp 联系我们，我们会协助您。',
    otherSignout: '用其他账户登录',
    otherSupport: '通过 WhatsApp 联系客服',
    waText: '您好，我需要帮助访问我们在 Maabar 的工厂主页。',
  },
  en: {
    working: 'Opening your conversation…',
    notFound: 'This link is not valid or has expired.',
    otherTitle: 'This factory page is registered to another account',
    otherBody: "We can't show any details about that account. If it belongs to your company, sign in with that account — or contact us on WhatsApp and we'll help.",
    otherSignout: 'Sign in with a different account',
    otherSupport: 'Contact support on WhatsApp',
    waText: 'Hello, I need help accessing our factory page on Maabar.',
  },
};

const wrap = { minHeight: 'var(--app-dvh,100vh)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'var(--bg-page,#FAF9F7)' };
const card = { width: '100%', maxWidth: 420, background: '#fff', border: '1px solid #E8E3DA', borderRadius: 16, padding: '28px 26px', boxShadow: '0 10px 40px rgba(26,24,20,0.08)', textAlign: 'center' };
const primary = { display: 'block', width: '100%', boxSizing: 'border-box', padding: '12px', marginTop: 14, border: 'none', borderRadius: 9, background: '#1A1814', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' };
const ghost = { display: 'block', width: '100%', boxSizing: 'border-box', padding: '12px', marginTop: 10, border: '1px solid rgba(0,0,0,0.16)', borderRadius: 9, background: 'transparent', color: 'rgba(0,0,0,0.7)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none' };

export default function FactoryInboxEntry({ lang = 'zh' }) {
  const { slug: rawSlug } = useParams();
  // Same tolerance as the claim page: a trailing '|' or stray whitespace pasted
  // from a copied cell must not turn a live link into a dead one.
  const slug = (rawSlug || '').replace(/[^A-Za-z0-9._-]/g, '');
  const nav = useNavigate();
  // Factory-facing surface: Chinese default, English the only alternative.
  const t = lang === 'en' ? C.en : C.zh;

  const [phase, setPhase] = useState('resolving');   // resolving | other_account | notfound

  useEffect(() => {
    let alive = true;
    (async () => {
      logClaimEvent(slug, 'inbox_entry_opened');

      // Session FIRST. get_factory_claim_preview computes is_owner as
      // `linked_supplier_id = auth.uid()`, so a preview sent before supabase-js has
      // restored the session returns is_owner=false for a genuine owner — the same
      // timing bug fixed on the claim page in ef102ab.
      let signedIn = false;
      let myId = null;
      try {
        const { data } = await sb.auth.getSession();
        myId = data?.session?.user?.id || null;
        signedIn = !!myId;
      } catch { /* unreadable session → treat as signed out */ }
      if (!alive) return;

      let pre = null;
      try { pre = await getFactoryClaimPreview(slug); } catch { pre = null; }
      if (!alive) return;
      if (!pre) { logClaimEvent(slug, 'inbox_entry_not_found'); setPhase('notfound'); return; }

      const nextPath = `/factory/inbox/${slug}`;
      const { action, to } = resolveFactoryEntry({
        signedIn,
        alreadyClaimed: !!pre.already_claimed,
        isOwner: !!pre.is_owner,
        claimSlug: slug,
        nextPath,
      });
      logClaimEvent(slug, 'inbox_entry_resolved', { action, signedIn });

      if (action === ENTRY.OTHER_ACCOUNT) { setPhase('other_account'); return; }
      if (action === ENTRY.CLAIM || action === ENTRY.LOGIN) { nav(to, { replace: true }); return; }

      // Owner: land on the conversation itself, on the unified `messages` stack.
      // There is no thread id there — a conversation is the (factory, trader)
      // pair — so take the newest trader on this factory. claim_factory_by_slug
      // is no longer called: it was only ever used for its thread_id, which
      // pointed at the retired factory_threads stack, and it is a no-op for an
      // existing owner anyway.
      let dest = '/dashboard';
      try {
        const th = await fetchLatestOwnerThreadForFactory(myId, pre.factory_id);
        if (th) dest = `/chat/f/${th.factory_id}/${th.trader_id}`;
      } catch (e) {
        logClaimEvent(slug, 'inbox_entry_owner_error', { message: String(e?.message || e).slice(0, 200) });
      }
      if (!alive) return;
      // Full load, not nav(): App.js caches the profile and ignores same-user
      // SIGNED_IN / TOKEN_REFRESHED, so a client-side nav can route on a stale role.
      window.location.assign(dest);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (phase === 'other_account') {
    return (
      <div style={wrap} dir="ltr">
        <div style={card}>
          <div style={{ fontSize: 26, marginBottom: 10 }}>🛡</div>
          <h1 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 10px', color: '#1A1814' }}>{t.otherTitle}</h1>
          <p style={{ fontSize: 13.5, lineHeight: 1.8, color: '#3D3A35', margin: '0 0 6px' }}>{t.otherBody}</p>
          <button
            style={primary}
            onClick={async () => { await sb.auth.signOut(); window.location.reload(); }}
          >
            {t.otherSignout}
          </button>
          <a style={ghost} href={waLink(t.waText)} target="_blank" rel="noreferrer">{t.otherSupport}</a>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap} dir="ltr">
      <div style={card}>
        <p style={{ fontSize: 13.5, color: '#3D3A35', margin: 0 }}>
          {phase === 'notfound' ? t.notFound : t.working}
        </p>
      </div>
    </div>
  );
}
